import React from 'react';
import { ArrowDownUp } from 'lucide-react';
import { CurrencyInput } from './CurrencyInput';
import { Button } from '../ui/Button';
import { AVAILABLE_CURRENCIES } from '../../types/converter';
import { formatRate } from '../../utils/format/currency';
import { ContentDisclaimer } from '../shared/ContentDisclaimer';

interface ConversionFormProps {
  fromAmount: number;
  fromCurrency: string;
  toCurrency: string;
  toAmount: number;
  rate: number;
  onFromAmountChange: (amount: number) => void;
  onFromCurrencyChange: (currency: string) => void;
  onToCurrencyChange: (currency: string) => void;
  onSwapCurrencies: () => void;
  onConvert: () => void;
}

export function ConversionForm({
  fromAmount,
  fromCurrency,
  toCurrency,
  toAmount,
  rate,
  onFromAmountChange,
  onFromCurrencyChange,
  onToCurrencyChange,
  onSwapCurrencies,
  onConvert,
}: ConversionFormProps) {
  // Round toAmount to 2 decimal places
  const roundedToAmount = Math.round(toAmount * 100) / 100;

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-100">
      <div className="relative h-32 bg-gradient-to-r from-emerald-600 to-emerald-500">
        <img
          src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e"
          alt="Currency exchange and conversion"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <span className="text-3xl font-bold">₦</span>
            <h2 className="text-xl font-bold ml-2 inline">Currency Converter</h2>
          </div>
        </div>
      </div>
      <div className="p-8">
      
      <div className="space-y-8">
        <CurrencyInput
          amount={fromAmount}
          currency={fromCurrency}
          label="From"
          onAmountChange={onFromAmountChange}
          onCurrencyChange={onFromCurrencyChange}
          currencies={AVAILABLE_CURRENCIES}
        />

        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onSwapCurrencies}
            className="rounded-full p-3 hover:bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-300"
          >
            <ArrowDownUp className="h-5 w-5 text-emerald-600" />
          </Button>
        </div>

        <CurrencyInput
          amount={roundedToAmount}
          currency={toCurrency}
          label="To"
          onAmountChange={() => {}}
          onCurrencyChange={onToCurrencyChange}
          currencies={AVAILABLE_CURRENCIES}
          readOnly
        />

        <Button
          variant="primary"
          className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 
                   hover:from-emerald-700 hover:to-emerald-600 
                   text-lg py-4 shadow-md font-heading font-semibold text-white"
          onClick={onConvert}
        >
          Convert Currency
        </Button>

        <div className="text-center bg-emerald-50 rounded-lg p-5 border border-emerald-100">
          <p className="text-sm text-emerald-700 font-medium mb-1">Current Exchange Rate</p>
          <p className="text-xl font-heading font-semibold text-emerald-800">
            1 {fromCurrency} = {formatRate(rate)} {toCurrency}
          </p>
        </div>
      </div>
      
      <ContentDisclaimer variant="compact" className="mt-6" />
      </div>
    </div>
  );
}